export interface MorningSlot { time: string; session: string; speaker?: string }
export interface AfternoonSlot { time: string; group1: string; group2: string }

export interface EventItem {
  id: string;
  title: string;
  phase: string;
  product: string;
  category: string;
  date: string;
  location: string;
  time: string;
  attendees: string;
  description: string;
  longDescription: string;
  partner: string;
  heroImage: string;
  productImages: string[];
  gallery: string[];
  highlights: string[];
  // Upcoming ("coming soon") events use these extra fields.
  upcoming?: boolean;
  bannerImage?: string;     // designed 16:9 promo banner, shown uncropped
  slug?: string;            // links to the DB event for registration
  registrationOpen?: boolean;
  fee?: number;             // registration fee in TZS (paid via ClickPesa); omit/0 = free
  venueNote?: string;
  programmeNote?: string;
  capacityNote?: string;
  morning?: MorningSlot[];
  afternoon?: AfternoonSlot[];
}

const phase1 = (n: number) =>
  `/images/Events/Cention n phase 1/Cention-workshop_${String(n).padStart(2, '0')}.jpg`;
const phase2 = (n: number) =>
  `/images/Events/Cention n phase 2/Cention-workshop2_${String(n).padStart(2, '0')}.jpg`;
const aesthetic = (n: number) =>
  `/images/Events/Aesthetic Dentistry/Aesthetic-Dentistry_${String(n).padStart(2, '0')}.jpg`;
const ksi = (n: number) =>
  `/images/Events/KSI meeting/KSI-meeting_${String(n).padStart(2, '0')}.jpg`;

export const events: EventItem[] = [
  {
    id: 'restorative-masterclass-2026',
    slug: 'restorative-masterclass-2026',
    upcoming: true,
    registrationOpen: true,
    fee: 70000,
    title: 'ITI Tanzania Launch & Implant Restorative Masterclass 2026',
    phase: 'Upcoming · CPD Workshop',
    product: 'Composites & Implants',
    category: 'Lectures',
    date: 'Saturday, 10 October 2026',
    location: 'Serena Hotel, Dar es Salaam',
    time: '8:30 AM - 4:00 PM',
    attendees: 'Up to 70',
    description:
      'A full day of clinical lectures for dental professionals in Dar es Salaam, marking the ITI Tanzania launch, in conjunction with Ivoclar. When to restore, when to implant, and how to do both well.',
    longDescription:
      'Join us for the ITI Tanzania Launch & Implant Restorative Masterclass 2026, a full-day CPD workshop in conjunction with Ivoclar at the Serena Hotel, Dar es Salaam. The programme brings together three expert speakers for clinical lectures spanning composite selection, implant overdentures, direct composites, and restoring function and aesthetics in the deficient anterior maxilla, and closes with an open ITI case discussion.',
    partner: 'in conjunction with Ivoclar',
    heroImage: '/images/Events/Restorative Masterclass-2026/Restorative Masterclass-2026.jpg?v=2',
    bannerImage: '/images/Events/Restorative Masterclass-2026/Restorative Masterclass-2026.jpg?v=2',
    productImages: [],
    gallery: [],
    capacityNote:
      'Limited to 70 delegates, allocated on a first-come, first-served basis.',
    // Full-day schedule (from Dr Haffajee's updated running order).
    morning: [
      { time: '08:30 - 09:00', session: 'Welcome' },
      { time: '09:00 - 10:00', session: 'The Practical Choice: Selecting the Perfect Composite for Every Cavity', speaker: 'Dr Leila Perea-Lowery' },
      { time: '10:00 - 11:00', session: 'Welcome to the ITI', speaker: 'Dr Ridhwaan Haffajee' },
      { time: '11:00 - 11:15', session: 'Tea break' },
      { time: '11:15 - 12:30', session: 'Implant Overdentures: Why, When and How?', speaker: 'Dr Ridhwaan Haffajee' },
      { time: '12:30 - 13:15', session: 'From Anterior Artistry to 4 mm Bulk Fills: Mastering Direct Composites with Tetric N2', speaker: 'Dr Leila Perea-Lowery' },
      { time: '13:15 - 14:00', session: 'Lunch' },
      { time: '14:00 - 15:00', session: 'Restoring Function and Aesthetics in the Deficient Anterior Maxilla: A Surgical Perspective', speaker: 'Dr Germana Lyimo' },
      { time: '15:15 - 16:00', session: 'ITI Case Discussion (whole group informal discussion)', speaker: 'All speakers' },
      { time: '16:15', session: 'Closing remarks' },
    ],
    highlights: [
      'Marks the launch of the ITI in Tanzania',
      'Clinical lectures from three expert speakers',
      'Composite selection and direct composite restorations',
      'Implant overdentures and restoring the deficient anterior maxilla',
      'Open ITI case discussion with all speakers',
      'In conjunction with Ivoclar',
    ],
  },
  {
    id: 'cention-phase-1',
    title: 'Cention N Event Phase 1',
    phase: 'Phase One',
    product: 'Cention N',
    category: 'Workshop',
    date: '8th March 2024',
    location: 'Serena Hotel, Dar es Salaam',
    time: '9:00 AM - 5:00 PM',
    attendees: '100+',
    description: 'A successful training event focused on Cention N, a cutting-edge dental material. The event brought together private and public sector hospitals in Tanzania, providing valuable insights and hands-on experience to dental professionals.',
    longDescription: 'This comprehensive workshop introduced dental professionals to Cention N, a revolutionary mercury-free filling material. The event featured hands-on training sessions, product demonstrations, and expert presentations from leading dental specialists. Participants gained practical experience with the material and learned about its applications in modern dentistry.',
    partner: 'in conjunction with Ivoclar',
    heroImage: phase1(1),
    productImages: [phase1(1), phase1(2)],
    gallery: Array.from({ length: 10 }, (_, i) => phase1(i + 1)),
    highlights: [
      'Hands-on training with Cention N restorative material',
      'Live product demonstrations by Ivoclar specialists',
      'Networking with public & private sector dental professionals',
      'Certificate of participation for all attendees',
    ],
  },
  {
    id: 'aesthetic-dentistry',
    title: 'Aesthetic Dentistry Workshop',
    phase: 'Advanced Training',
    product: 'Aesthetic Dentistry',
    category: 'Training',
    date: '15th March 2024',
    location: 'Hilton Hotel, Dar es Salaam',
    time: '8:00 AM - 6:00 PM',
    attendees: '50+',
    description: 'Advanced training in cosmetic dental procedures and smile design techniques for dental professionals across East Africa.',
    longDescription: 'This intensive workshop covered the latest techniques in aesthetic dentistry, including smile design, veneer placement, and cosmetic bonding procedures. Participants learned from internationally recognized experts and gained hands-on experience with state-of-the-art equipment.',
    partner: 'in partnership with leading manufacturers',
    heroImage: aesthetic(1),
    productImages: [aesthetic(1), aesthetic(2)],
    gallery: Array.from({ length: 5 }, (_, i) => aesthetic(i + 1)),
    highlights: [
      'Smile design & digital workflow training',
      'Veneer placement and cosmetic bonding techniques',
      'Sessions led by internationally recognized experts',
      'State-of-the-art equipment demonstrations',
    ],
  },
  {
    id: 'cention-phase-2',
    title: 'Cention N Event Phase 2',
    phase: 'Phase Two',
    product: 'Cention N',
    category: 'Workshop',
    date: '22nd April 2024',
    location: 'Regional Centers, Tanzania',
    time: '9:00 AM - 5:00 PM',
    attendees: '75+',
    description: 'Advanced techniques and hands-on practice with Cention N restorative materials for experienced dental professionals.',
    longDescription: 'Building on the foundation from Phase 1, this advanced workshop focused on complex restorative procedures using Cention N. Participants learned advanced placement techniques, finishing procedures, and troubleshooting common challenges.',
    partner: 'in conjunction with Ivoclar',
    heroImage: phase2(1),
    productImages: [phase2(1), phase2(2)],
    gallery: Array.from({ length: 6 }, (_, i) => phase2(i + 1)),
    highlights: [
      'Advanced placement & finishing techniques',
      'Complex restorative case walkthroughs',
      'Troubleshooting common clinical challenges',
      'Regional accessibility across Tanzania',
    ],
  },
  {
    id: 'ksi-meeting',
    title: 'KSI Knowledge Sharing Meeting',
    phase: 'Industry Collaboration',
    product: 'Knowledge Sharing',
    category: 'Conference',
    date: '10th May 2024',
    location: 'Nairobi, Kenya',
    time: '10:00 AM - 4:00 PM',
    attendees: '30+',
    description: 'Knowledge sharing and industry collaboration with dental professionals from across East Africa.',
    longDescription: 'This exclusive conference brought together key stakeholders in the dental industry for knowledge sharing, networking, and collaborative discussions on advancing dental care in the region.',
    partner: 'in collaboration with KSI',
    heroImage: ksi(1),
    productImages: [ksi(1), ksi(2)],
    gallery: Array.from({ length: 2 }, (_, i) => ksi(i + 1)),
    highlights: [
      'Cross-border knowledge sharing across East Africa',
      'Networking with key industry stakeholders',
      'Collaborative discussions on advancing dental care',
      'Exclusive, invitation-based conference',
    ],
  },
];

export const getEventById = (id: string) => events.find(e => e.id === id);
